import {
  ChangeEvent,
  DragEvent,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  useId,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { IconButton } from "./Button";
import { HiddenInput } from "./Form";

export interface FileUploadRootProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  inputAriaLabel?: string;
}

export interface FileUploadProps extends FileUploadRootProps {
  label?: string;
  description?: string;
  variant?: "standard" | "interactive" | "document";
  size?: "md" | "lg";
  icon?: ReactNode;
}

const HiddenFileInput = styled(HiddenInput)`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: calc(-1 * var(--inzeloop-ds-spacing-1px));
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const FileUploadBehaviorRoot = styled.div``;

export const FileUploadRoot = ({
  onFilesSelected,
  accept,
  multiple = false,
  disabled = false,
  inputAriaLabel,
  children,
  onClick,
  onKeyDown,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  ...containerProps
}: FileUploadRootProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const [dragging, setDragging] = useState(false);

  const emitFiles = (files: FileList | null) => {
    if (disabled || !files?.length) return;
    onFilesSelected(Array.from(files));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    emitFiles(event.target.files);
    event.target.value = "";
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (
      event.defaultPrevented ||
      disabled ||
      (event.key !== "Enter" && event.key !== " ")
    ) {
      return;
    }
    event.preventDefault();
    inputRef.current?.click();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    onDrop?.(event);
    if (event.defaultPrevented && event.dataTransfer.files.length === 0) return;
    event.preventDefault();
    setDragging(false);
    emitFiles(event.dataTransfer.files);
  };

  return (
    <FileUploadBehaviorRoot
      {...containerProps}
      role="button"
      tabIndex={disabled ? -1 : (containerProps.tabIndex ?? 0)}
      aria-disabled={disabled}
      aria-controls={generatedId}
      data-dragging={dragging}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented && !disabled) inputRef.current?.click();
      }}
      onKeyDown={handleKeyDown}
      onDragEnter={(event) => {
        onDragEnter?.(event);
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragOver={(event) => {
        onDragOver?.(event);
        event.preventDefault();
      }}
      onDragLeave={(event) => {
        onDragLeave?.(event);
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDragging(false);
        }
      }}
      onDrop={handleDrop}
    >
      <HiddenFileInput
        ref={inputRef}
        id={generatedId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-label={inputAriaLabel}
        onChange={handleChange}
        tabIndex={-1}
      />
      {children}
    </FileUploadBehaviorRoot>
  );
};

const UploadArea = styled(FileUploadRoot)<{
  $variant: "standard" | "interactive" | "document";
  $size: "md" | "lg";
}>`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-height: ${({ $size }) => ($size === "lg" ? vars.spacing.px190 : "128px")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  padding: ${vars.spacing.xl};
  border: ${vars.borderWidth.thin} dashed ${vars.color.border.medium};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.surface};
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  text-align: center;
  cursor: default;

  ${({ $variant }) =>
    $variant === "document" &&
    css`
      border-radius: ${vars.radius.none};
      background: ${vars.color.background.subtle};
    `}

  ${({ $variant }) =>
    ($variant === "interactive" || $variant === "document") &&
    css`
      border-style: ${$variant === "document" ? "dashed" : "solid"};
      transition:
        background-color ${vars.motion.fast} ${vars.motion.easing},
        border-color ${vars.motion.fast} ${vars.motion.easing};

      &:not([aria-disabled="true"]):hover,
      &:not([aria-disabled="true"]):active {
        border-color: ${vars.color.border.strong};
        background: ${vars.color.background.subtle};
      }
    `}

  &[data-dragging="true"] {
    border-color: ${vars.color.border.accent};
    background: ${vars.color.background.subtle};
  }

  &[aria-disabled="true"] {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }
`;

const UploadLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.sm};
`;

const UploadIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const UploadDescription = styled.span`
  color: ${vars.color.text.muted};
  font-size: ${vars.typography.size.xs};
  line-height: ${vars.typography.lineHeight.xs};
  white-space: pre-line;
`;

export const FileUpload = ({
  onFilesSelected,
  label = "Choose files or drop them here",
  description,
  accept,
  multiple = false,
  disabled = false,
  variant = "standard",
  size = "md",
  icon,
  ...containerProps
}: FileUploadProps) => {
  return (
    <UploadArea
      {...containerProps}
      $variant={variant}
      $size={size}
      onFilesSelected={onFilesSelected}
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      aria-label={label}
    >
      <UploadLabel>
        {icon && <UploadIcon aria-hidden="true">{icon}</UploadIcon>}
        {label}
      </UploadLabel>
      {description && <UploadDescription>{description}</UploadDescription>}
    </UploadArea>
  );
};

export const FileDropArea = styled(FileUploadRoot)<{
  $alignLeft?: boolean;
  $width?: string;
  $height?: string;
}>`
  display: flex;
  width: ${({ $width }) => $width || "670px"};
  min-height: ${({ $alignLeft, $height }) =>
    $alignLeft ? $height || "70px" : "140px"};
  height: auto;
  box-sizing: border-box;
  flex-direction: column;
  align-items: ${({ $alignLeft }) => ($alignLeft ? "flex-start" : "center")};
  justify-content: flex-start;
  gap: ${({ $alignLeft }) =>
    $alignLeft ? vars.spacing.xs : "var(--inzeloop-ds-spacing-md)"};
  overflow: hidden;
  padding: ${({ $alignLeft }) =>
    $alignLeft
      ? `${vars.spacing.xs} ${vars.spacing.sm}`
      : `${vars.spacing.lg} 12px`};
  border: ${vars.borderWidth.thin} dashed ${vars.color.border.medium};
  background: ${vars.color.background.subtle};
  cursor: default;
`;

export const FileDropTitle = styled.div`
  width: 100%;
  flex: none;
  padding: 0;
  margin: 0;
  color: ${vars.color.text.accent};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.xs};
  text-align: center;
`;

export const FileDropDescription = styled.div`
  width: 100%;
  align-self: stretch;
  padding: 0;
  margin: 0;
  color: ${vars.color.text.muted};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
  text-align: center;
  white-space: normal;
`;

export const FileList = styled.div`
  display: flex;
  width: 100%;
  max-height: 120px;
  box-sizing: border-box;
  flex-direction: column;
  gap: ${vars.spacing.xxs};
  overflow-y: auto;
  padding: 0;
  margin: 0;
`;

export const FileRow = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: var(--inzeloop-ds-spacing-md);
`;

export const FileName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
  margin: 0;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.regular};
  line-height: var(--inzeloop-ds-line-height-20px);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--inzeloop-ds-spacing-md);
`;

export const FileActionButton = styled(IconButton).attrs({ size: "sm" })``;

export const FileRemoveButton = styled(FileActionButton).attrs({
  variant: "danger",
})``;

export const FileOverlayContent = styled.div`
  position: absolute;
  right: 20px;
  left: 10px;
  display: flex;
  align-items: flex-start;
  gap: var(--inzeloop-ds-spacing-md);
  padding-top: var(--inzeloop-ds-spacing-10px);
  border-radius: ${vars.radius.lg};
  background: ${vars.color.background.surface};
  pointer-events: none;
`;

export const FileOverlayBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  border-radius: ${vars.radius.xs} ${vars.radius.xs} 0 0;
  background: var(--inzeloop-ds-color-status-negative-accent);
  color: ${vars.color.text.inverse};
  font-size: var(--inzeloop-ds-font-size-13px);
  font-weight: ${vars.typography.weight.semibold};
`;

export const FileOverlayIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${vars.spacing.xs} ${vars.spacing.sm} ${vars.spacing.sm};
  border-radius: 0 0 ${vars.radius.xs} ${vars.radius.xs};
  background: ${vars.color.background.surface};
  box-shadow: ${vars.shadow.md};
`;

export const FileOverlayName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  line-height: var(--inzeloop-ds-line-height-relaxed);
`;
